import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button, OutlineButton, SubtleButton } from "components/button";
import Field from "components/field";
import MultiInput from "components/input/multi";
import { useModalContext } from "components/modal";
import Assignee from "components/react-select/assignees";
import { useSnackbarContext } from "components/snackbar";
import CustomDocModal from "containers/customDocModal";
import AdditionalTerms from "containers/AdditionalTerms";
import { useMutation, useQuery, useQueryClient } from "react-query";
import TooltippedElement from "components/TooltippedElement";
import { FaInfoCircle } from "react-icons/fa";
import { MdAttachFile } from "react-icons/md";
import FileGrid from "components/file-grid";
import { generatePdfThumbnail } from "components/pdf-thumbnail";
import { useTranslation } from 'react-i18next';
import { TProperty } from "types/property";
import { activeAdditionalTermsForPayload } from "utils/additionalTermsPayload";
import { normalizeCustomTextForPayload } from "utils/customTextPayload";

type Props = {
  emails: string[] | null;
  estimateId: string;
  requestOrProject: string;
  requestOrProjectId: string;
};

export default function SendEstimateModal(props: Props) {
  const { t } = useTranslation();
  const tBase = "views.estimate.sendEstimateModal";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const { showSnackbar } = useSnackbarContext();
  const queryClient = useQueryClient();
  const modal = useModalContext();

  const [templateId, setTemplateId] = useState<string>();
  const [emails, setEmails] = useState<(string | null)[] | null>();
  const [customText, setCustomText] = useState<string>("");
  const [manager, setManager] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [noColor, setNoColor] = useState<boolean>(false);
  const [additionalTerms, setAdditionalTerms] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [billing, setBilling] = useState<TProperty>();
  const [showAll, setShowAll] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);

  const type = props.requestOrProject;
  const typeId = props.requestOrProjectId;

  const getManagerQuery = useQuery<{ value: string; label: string } | null>(
    ["manager", "estimate", props.estimateId],
    async () =>
      await axios
        .get(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/company/estimates/${props.estimateId}/manager
  `
        )
        .then((res) => res.data),
    {
      onSuccess: (data) => {
        setManager(data);
      },
    }
  );

  const { refetch: refetchFiles } = useQuery(
    ["requestOrProjectFiles", type, typeId],
    async () => {
      return await axios
        .get(`
          ${process.env.REACT_APP_SERVER_URL}/api/v1/company/files/${type}/${typeId}
        `)
        .then((res) => res.data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: false
    }
  );

  const mutation = useMutation(
    async () => {
      const customTextPayload = normalizeCustomTextForPayload(customText);
      const formData = new FormData();

      attachments.forEach((file) => {
        if (file.new) { // new file
          if (file.editedName) {
            const newFile = new File([file.fullFile], file.editedName, { type: file.fullFile.type });
            const newThumbnail = new File([file.thumbnailFile], file.editedName, { type: file.thumbnailFile.type });
        
            formData.append("attachments", newFile);
            formData.append("thumbnails", newThumbnail);
          } else {
            formData.append("attachments", file.fullFile); 
            formData.append("thumbnails", file.thumbnailFile); 
          }
        } else { // fetched from request/project
          formData.append("fetchedFiles", JSON.stringify({ 
            name: file.name,
            editedName: file.editedName || "",
            key: file.name, 
            url: file.url,
            type,
            typeId
          }));
        }
      });
      
      formData.append("templateId", templateId || "");
      formData.append("emails", JSON.stringify(emails ?? []));
      formData.append("customText", customTextPayload);
      formData.append("fk_manager_id", manager?.value || "");
      formData.append("noColor", noColor.toString());
      formData.append(
        "additionalTerms",
        JSON.stringify(activeAdditionalTermsForPayload(additionalTerms))
      );
      formData.append("type", type);
      formData.append("typeId", typeId);
      formData.append("billing", JSON.stringify(billing));

      return await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/estimates/${props.estimateId}/send`,
        formData
      );
    },
    {
      onSuccess: () => {
        showSnackbar(tr("Estimate sent!"));
        queryClient.invalidateQueries();
        modal.setModal();
      },
      onError: () => {
        showSnackbar(tr("Error sending estimate"));
      },
    }
  );

  const preview = useMutation(
    async () =>
      await axios
        .post(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/estimates/${props.estimateId}/preview`,
          {
            templateId,
            customText: normalizeCustomTextForPayload(customText),
            additionalTerms: activeAdditionalTermsForPayload(additionalTerms),
            fk_manager_id: manager?.value,
            noColor,
            billing
          }
        )
        .then((res) => res.data),
    {
      onSuccess: (url) => {
        window.open(url, "_blank");
      },
      onError: () => {
        showSnackbar(tr("Error previewing estimate"));
      },
    }
  );

  const handleTermEdit = (id: string, updatedTerm: { text?: string; active?: boolean, type?: string, order?: number }) => {
    setAdditionalTerms((prev) =>
      prev.map((term) =>
        term.id === id
          ? { ...term, ...updatedTerm } // Update both text and active properties
          : term
      )
    );
  };

  const handleTermsLoad = (terms: any[]) => {
    setAdditionalTerms((prev) =>
      terms.map((term) => {
        // Retain edits or use the loaded data
        const existingTerm = prev.find((t) => t.id === term.id);
        return {
          ...term,
          active: existingTerm?.active ?? term.active ?? true,
          text: existingTerm?.text ?? term.text,
          type: existingTerm?.type ?? term.type,
          order: existingTerm?.order ?? term.order
        };
      })
    );
  };

  useEffect(() => {
    if (props.emails) {
      // split: '1@mail.com, 2@mai.com' into '1@mail.com', '2@mai.com' and trim whitespaces
      const emailStrings = props.emails.join(",").split(",");
      const fixedStringEmails = emailStrings.map((email) => email.trim());
      setEmails(fixedStringEmails);
    }
  }, [props.emails]);

  const handleEditStatusChange = (isEditing: boolean) => {
    setIsEditing(isEditing);
  };

  const attachmentUpload = () => {
    inputRef.current?.click();
  };

  // handles new files
  const handleFileSelection = (files: FileList) => {
    try {
      setAttachments((prev) => {
        const validExtensions = [".pdf", ".png", ".jpg", ".jpeg"];
        const uniqueFiles = Array.from(files).filter((file) => {
          const fileExtension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
          const isValidExtension = validExtensions.includes(fileExtension);
          const isUnique = !prev.some((existingFile) => existingFile.name === file.name);

          if (!isValidExtension) {
            showSnackbar(tr("Invalid file type, supported types are") + ": PDF, PNG, JPG, JPEG");
          } else if (!isUnique) {
            showSnackbar(tr("File already added"));
          }

          return isValidExtension && isUnique;
        });

        const processedFiles = uniqueFiles.map(async (file) => {
          if (file.type.startsWith("image/")) {
            return {
              name: file.name,
              type: file.type,
              url: URL.createObjectURL(file), // For images, use a preview URL
              thumbnail: URL.createObjectURL(file),
              thumbnailFile: file,
              new: true,
              fullFile: file,
            };
          } else if (file.type === "application/pdf") {
            const thumbnail = await generatePdfThumbnail(file);
            const thumbnailBlob = thumbnail && await fetch(thumbnail).then((res) => res.blob());
            const thumbnailFile = thumbnailBlob && new File([thumbnailBlob], file.name, { type: "image/png" });
            return {
              name: file.name,
              type: file.type,
              url: URL.createObjectURL(file), // Thumbnail for PDF
              thumbnail: thumbnail, // Preview URL for PDF
              thumbnailFile: thumbnailFile,
              new: true,
              fullFile: file,
              isPdf: true,
            };
          } else {
            return {
              name: file.name,
              type: file.type,
              url: null, // Unsupported file types
              thumbnail: null,
              new: true,
              fullFile: file,
            };
          }
        });
    
        // Wait for all thumbnail generation to complete
        Promise.all(processedFiles).then((finalFiles) => {
          setAttachments([...prev, ...finalFiles]);
        });
    
        return prev;
      });
    } catch (e) {
      showSnackbar(tr("Error adding file(s)"));
    }
  };

  const addFiles = async () => {
    try {
      const { data: fetchedFiles } = await refetchFiles(); 
      if (fetchedFiles) {
        setAttachments((prev) => {
          const uniqueFiles = fetchedFiles
            .filter((file: any) => file.signedUrl) 
            .map((file: any) => ({
              name: file.Key, 
              url: file.signedUrl, 
              type: file.type, 
              size: file.Size, 
              lastModified: file.LastModified, 
            }))
            .filter((file: any) => 
              !prev.some((existingFile) => existingFile.name === file.name) // Prevent duplicates
            );
  
          return [...prev, ...uniqueFiles];
        });
      }
    } catch (error) {
      showSnackbar(tr("Error adding file(s)"));
      console.error("Error fetching files:", error);
    }
  };

  const billingAddress = useQuery<TProperty[] | null>(
    ["billing address"],
    async () =>
      await axios
        .get(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/company/estimates/${props.estimateId}/billing_address
  `
        )
        .then((res) => res.data)
  );

  const visibleAddresses = showAll
    ? billingAddress.data
    : billingAddress.data?.slice(0, 5);

  const populateBillingAddresses = visibleAddresses?.map((address) => {
    return (
      <label key={address.id} className="flex items-center space-x-2 space-y-2">
        <input
          type="radio"
          value={address.id}
          name="billing_address"
          onChange={(e: any) => {
            setBilling(address);
          }}
          checked={billing?.id === address.id}
          className="mt-2.5"
        />
        <div className="text-wrap">
          {address.full_address}
          {address.name && (
            <span className="text-gray-500 italic"> ({address.name})</span>
          )}
        </div>
      </label>
    );
  });

  return (
    <div className="max-w-[500px] space-y-4 mr-2">
      <MultiInput values={emails} onChange={(e) => setEmails(e)} />
      <CustomDocModal
        id={templateId}
        type="estimate"
        onClick={(id) => setTemplateId((tId) => (tId === id ? undefined : id))}
      />
      <AdditionalTerms
        id={templateId}
        onEdit={(id, updatedTerm) => handleTermEdit(id, updatedTerm)} // Pass the updated term object
        onLoad={handleTermsLoad}
        onEditStatusChange={handleEditStatusChange}
      />
      <div className="my-2">
        <h3>{tr("Billing Address")}</h3>
        <div className="flex flex-col">{populateBillingAddresses}</div>
      </div>
      <div className="flex justify-center">
        {billingAddress && (billingAddress.data ?? []).length > 5 && (
          <button
            type="button"
            onClick={() => setShowAll(!showAll)}
            className="text-blue-600 hover:underline text-sm"
          >
            {showAll ? tr("Show Less") : tr("Show More")}
          </button>
        )}
      </div>
      <Field
        label={tr("Custom Text")}
        type="textarea"
        minRows={4}
        value={customText}
        onChange={(e) => setCustomText(e.target.value)}
      />
      <Assignee
        label={tr("Manager")}
        value={manager}
        isMulti={false}
        menuPlacement="top"
        onChange={(e: any) => setManager(e)}
      />
      <div className="flex items-center">
        <input
          type="checkbox"
          checked={noColor}
          onChange={() => setNoColor(!noColor)}
        />
        <label className="ml-2">{tr("Black & White Print")}</label>
        <TooltippedElement
          message={tr("Turn Off Color for Printing PDF")}
          placement="top"
          element={<FaInfoCircle className="ml-1 text-secondary" />}
        />
      </div>
      {/* Attachments */}
      <div className="mt-2">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[12.8px]">{tr("Attachments")}:</div>
          {attachments.length > 0 && (
            <div>
              <button
                className="text-xs inline-block text-red-400 underline hover:no-underline"
                onClick={() => setAttachments([])}
                type="button"
              >
                {tr("Remove All Attachments")}
              </button>
            </div>
          )}
        </div>
        {attachments.length > 0 ? (
          <FileGrid
            id={typeId}
            type={type}
            files={attachments}
            setFiles={setAttachments}
            refetch={refetchFiles}
            isLocal={true}
          />
        ) : (
          <div className="text-gray-500">{tr("No attachments")}</div>
        )}
      </div>
      <div className=" h-[10px]" />
      {/* Buttons */}
      <div className="flex justify-end space-x-2 pb-2">
        <TooltippedElement
          message={`${type === "request" ? tr("Add files from request") : tr("Add files from project")}`}
          placement="top"
          element={
            <Button onClick={() => addFiles()} className="relative">
              <MdAttachFile />
              <div className="absolute top-0 right-2 text-[16px]">+</div>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-[8px]">
                {type === "request" ? tr("Request") : tr("Project")}
              </div>
            </Button>
          }
        />
        <Button onClick={attachmentUpload}>
          <MdAttachFile />
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg"
          onClick={(e) => {
            (e.target as HTMLInputElement).value = ""; // Reset file input
          }}
          onChange={(e) => {
            if (e.target.files) {
              handleFileSelection(e.target.files);
            }
          }}
          className="hidden"
        />
        <SubtleButton
          isLoading={preview.isLoading}
          disabled={!templateId || !manager || preview.isLoading || isEditing || !          billing}
          primary
          onClick={() => preview.mutate()}
        >
          {tr("Preview")}
        </SubtleButton>
        <Button
          primary
          type="submit"
          isLoading={mutation.isLoading}
          disabled={
            emails?.length === 0 || !templateId || !manager || isEditing || !billing
          }
          onClick={() => mutation.mutate()}
        >
          {tr("Send Estimate")}
        </Button>
        <OutlineButton onClick={() => modal.setModal()}>
          {tr("Cancel")}
        </OutlineButton>
      </div>
    </div>
  );
}
